import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="documents--items-manager"
// Manages invoice item lifecycle: removal, ordering, and counter notifications
export default class extends Controller {
  static outlets = ["documents--items-counter", "documents--totals-calculator"]

  static values = {
    documentType: String
  }

  connect() {
    // Hide already-removed items on connect
    this.#hideRemovedItems()
  }

  // Public API: Remove an item
  removeItem(event) {
    const targetRow = event.target.closest("tr.invoice-item")
    if (!targetRow) return

    // Find the destroy input for this row
    const destroyInput = targetRow.querySelector('input[type="hidden"][name*="_destroy"]')
    if (destroyInput) {
      destroyInput.value = true
    }

    // Hide the row
    targetRow.classList.add("d-none")

    // Hide the description row for this invoice item
    this.#hideDescriptionRow(targetRow)

    // Update row order numbers
    this.#updateRowsOrder(targetRow)

    // Notify other controllers
    this.#notifyItemsCounter()
    this.#notifyTotalsCalculator()
    // Setting `_destroy` fires no input event, so anything that reads the form
    // (the settlement diff preview) listens for this instead.
    this.dispatch("itemRemoved", { target: targetRow })
  }

  // Update row order numbers when an item is removed
  #updateRowsOrder(deletedRow) {
    const rowsAfterTarget = []
    let nextElement = deletedRow.nextElementSibling

    // Traverse through all the siblings and collect them
    while (nextElement) {
      // Only item rows carry a line number. Rails also emits hidden id inputs
      // between rows on edit, and every item has sub-rows (discount,
      // description, an expense's Odpočet row) that share its data-index — a
      // sub-row counted here decremented that line a second time.
      if (nextElement.tagName === "TR" && nextElement.classList.contains("invoice-item")) {
        rowsAfterTarget.push(nextElement)
      }
      nextElement = nextElement.nextElementSibling
    }

    rowsAfterTarget.forEach((row) => {
      const orderElement = document.getElementById(
        `${this.documentTypeValue}_document_items_attributes_${row.dataset.index}_line_number`
      )
      if (!orderElement) {
        // Try expense items format
        const expenseOrderElement = document.getElementById(
          `${this.documentTypeValue}_expense_items_attributes_${row.dataset.index}_line_number`
        )
        if (expenseOrderElement) {
          expenseOrderElement.value = parseInt(expenseOrderElement.value, 10) - 1
        }
      } else {
        orderElement.value = parseInt(orderElement.value, 10) - 1
      }
    })
  }

  // Hide every sub-row of an invoice item (discount, description, an expense's
  // Odpočet row): all the rows up to the next item row.
  #hideDescriptionRow(invoiceItemRow) {
    let nextRow = invoiceItemRow.nextElementSibling

    while (nextRow && !nextRow.classList.contains("invoice-item")) {
      if (nextRow.tagName === "TR") nextRow.classList.add("d-none")
      nextRow = nextRow.nextElementSibling
    }
  }

  // Hide items that are already marked for destruction
  #hideRemovedItems() {
    // Select all hidden inputs with _destroy for invoice items rows
    const destroyFields = document.querySelectorAll('input[type="hidden"][name*="_destroy"]')

    // Iterate over the fields and check their value
    destroyFields.forEach((field) => {
      if (field.value === "true") {
        const row = field.closest("tr.invoice-item")
        if (row) {
          row.classList.add("d-none")
          // Also hide description row
          this.#hideDescriptionRow(row)
        }
      }
    })
  }

  // Notify items counter to update count
  #notifyItemsCounter() {
    if (this.hasDocumentsItemsCounterOutlet) {
      this.documentsItemsCounterOutlet.updateCount()
    }
  }

  // Notify totals calculator to recalculate
  #notifyTotalsCalculator() {
    if (this.hasDocumentsTotalsCalculatorOutlet) {
      this.documentsTotalsCalculatorOutlet.recalculate()
    }
  }
}
